import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";

import ConfirmationDialog from "../../components/ConfirmationDialog";
import CustomDatePicker from "../../components/Inputs/CustomDatePicker";
import { CustomTextField } from "../../components/Inputs/CustomTextField";
import WeekDaySelector from "../../components/Inputs/WeekDaySelector";
import { ArrivalRow } from "../types";

let rowCounter = 0;
const newKey = () => `new-${++rowCounter}`;

interface Props {
  rows: ArrivalRow[];
  onChange: (rows: ArrivalRow[]) => void;
  year: number;
}

export default function ArrivalManagement({ rows, onChange, year }: Props) {
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const markDirty = (updated: ArrivalRow[]) => onChange(updated);

  const updateRow = (index: number, field: keyof ArrivalRow, value: string) => {
    const updated = rows.map((r, i) =>
      i === index ? { ...r, [field]: value, persisted: false } : r
    );
    markDirty(updated);
  };

  const addRow = () => {
    const last = rows[rows.length - 1];
    const hasEmpty =
      last && (!last.arrivalDay || !last.startDate || !last.endDate);
    if (hasEmpty) return;
    markDirty([
      ...rows,
      {
        _key: newKey(),
        arrivalDay: "6",
        startDate: "",
        endDate: "",
        inventory: "",
        persisted: false,
      },
    ]);
  };

  const confirmDelete = (index: number) => {
    setDeleteIndex(index);
  };

  const doDelete = () => {
    if (deleteIndex === null) return;
    const updated = rows.filter((_, i) => i !== deleteIndex);
    setDeleteIndex(null);
    markDirty(updated);
  };

  const minDate = useMemo(
    () => DateTime.fromObject({ year, month: 1, day: 1 }),
    [year]
  );

  return (
    <Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: "text.secondary" }}>Jour</TableCell>
            <TableCell sx={{ color: "text.secondary" }}>Date début</TableCell>
            <TableCell sx={{ color: "text.secondary" }}>Date fin</TableCell>
            <TableCell sx={{ color: "text.secondary" }}>Stock</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row._key}>
              <TableCell>
                <WeekDaySelector
                  value={row.arrivalDay}
                  onChange={(val) => updateRow(index, "arrivalDay", val)}
                />
              </TableCell>
              <TableCell>
                <CustomDatePicker
                  label=""
                  translateLabel={false}
                  value={row.startDate || null}
                  minDate={minDate}
                  onChange={(val) => updateRow(index, "startDate", val ?? "")}
                />
              </TableCell>
              <TableCell>
                <CustomDatePicker
                  label=""
                  translateLabel={false}
                  value={row.endDate || null}
                  minDate={
                    row.startDate ? DateTime.fromISO(row.startDate) : minDate
                  }
                  onChange={(val) => updateRow(index, "endDate", val ?? "")}
                />
              </TableCell>
              <TableCell>
                <CustomTextField
                  type="number"
                  value={row.inventory}
                  onChange={(e) =>
                    updateRow(index, "inventory", e.target.value)
                  }
                  required={false}
                />
              </TableCell>
              <TableCell>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="Supprimer la ligne"
                  onClick={() => confirmDelete(index)}
                >
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={5} align="center">
              <IconButton
                onClick={addRow}
                color="primary"
                aria-label="Ajouter une ligne"
              >
                <AddIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <ConfirmationDialog
        open={deleteIndex !== null}
        title="Confirmer la suppression"
        message="Toutes les données relatives à la période de la ligne sélectionnée seront supprimées sans possibilité de retour en arrière (capacités, prix, contraintes tarifaires, optimisations...)."
        isLoading={false}
        onClose={() => setDeleteIndex(null)}
        onSubmit={doDelete}
      />
    </Box>
  );
}
